<template>
  <main-template>
    <v-row>
      <v-col class="d-flex justify-space-between align-center">
        <span class="text-h4">Desenvolvimento Biblioteca</span>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <e-autocomplete
          id="biblioteca.buscaAluno"
          :items="matriculas"
          :return-object="true"
          :item-value="(m) => m.matricula_id"
          :item-text="itemTextAluno"
          :loading="carregandoMatriculas"
          :value="matriculaSelecionada"
          label="Selecione o aluno..."
          dense
          clearable
          @change="onSelecionarAluno"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-btn
          v-if="$can(['desenvolvimentoBiblioteca.imprimir', 'desenvolvimentoBiblioteca.*'])"
          color="primary lighten-1"
          dark
          block
          @click="imprimirColetivo"
        >
          <v-icon left small>fa fa-file-pdf</v-icon>
          Imprimir coletivo
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="matricula">
      <v-col cols="12" class="pt-0">
        <v-alert dense text color="primary" class="mb-2">
          <strong>Legenda:</strong>
          <v-chip x-small color="green" dark class="mx-1">D</v-chip> Desenvolvido
          <v-chip x-small color="orange" dark class="mx-1">ED</v-chip> Em Desenvolvimento
          <v-chip x-small color="red" dark class="mx-1">ND</v-chip> Não Desenvolvido
        </v-alert>
      </v-col>
    </v-row>

    <v-card v-if="matricula" class="mt-1">
      <v-card-title class="primary white--text py-2">
        <div>
          <div class="text-subtitle-1">
            {{ matricula.aluno ? matricula.aluno.nomecompleto : "- - -" }}
            <v-chip
              v-if="matricula.aluno && matricula.aluno.especial"
              x-small
              color="blue"
              dark
              class="ml-2"
              >PcD</v-chip
            >
          </div>
          <div class="caption">
            Código: {{ matricula.aluno ? matricula.aluno.codigo || "- - -" : "- - -" }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          v-if="$can(['desenvolvimentoBiblioteca.imprimir', 'desenvolvimentoBiblioteca.*'])"
          color="primary lighten-1"
          :class="{ 'btn-travado': !temRegistroSalvo }"
          :dark="temRegistroSalvo"
          :disabled="!temRegistroSalvo"
          :title="temRegistroSalvo ? 'Imprimir individual' : 'Salve ao menos um item para imprimir'"
          @click="imprimir"
        >
          <v-icon left>fa fa-file-pdf</v-icon> Imprimir individual
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-for="grupo in grupos" :key="grupo">
          <div class="text-subtitle-1 font-weight-bold primary--text mt-3 mb-1">
            {{ grupo }}
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Item</th>
                  <th style="width: 220px">1º Semestre</th>
                  <th style="width: 220px">2º Semestre</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in itensDoGrupo(grupo)" :key="item.key">
                  <td>{{ item.label }}</td>
                  <td>
                    <v-select
                      :id="`biblioteca.${item.key}.s1`"
                      v-model="valores[item.key].indicador_s1"
                      :items="opcoesDoItem(item)"
                      item-text="text"
                      item-value="value"
                      dense
                      hide-details
                      clearable
                      placeholder="- - -"
                    />
                  </td>
                  <td>
                    <v-select
                      :id="`biblioteca.${item.key}.s2`"
                      v-model="valores[item.key].indicador_s2"
                      :items="opcoesDoItem(item)"
                      item-text="text"
                      item-value="value"
                      dense
                      hide-details
                      clearable
                      placeholder="- - -"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>

        <div class="text-right mt-4">
          <v-btn
            id="biblioteca.salvarBtn"
            color="primary"
            :dark="salvarHabilitado"
            :loading="salvando"
            :disabled="!salvarHabilitado"
            @click="salvar"
          >
            Salvar
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-alert v-else type="info" text border="left" color="primary" class="mt-4">
      Selecione um aluno para lançar o desenvolvimento.
    </v-alert>
  </main-template>
</template>

<script>
const ITENS = [
  {
    key: "leitura_reconhece_letras",
    grupo: "Leitura",
    label: "Reconhece letras do alfabeto",
    tipo: "ded",
  },
  {
    key: "leitura_identifica_nome",
    grupo: "Leitura",
    label: "Identifica o próprio nome e palavras familiares",
    tipo: "ded",
  },
  {
    key: "leitura_interesse_textos",
    grupo: "Leitura",
    label: "Demonstra interesse por textos e livros",
    tipo: "ded",
  },
  {
    key: "leitura_reconhece_silabas",
    grupo: "Leitura",
    label: "Reconhece sílabas e palavras simples",
    tipo: "ded",
  },
  {
    key: "leitura_inicia_leitura",
    grupo: "Leitura",
    label: "Inicia a leitura de pequenos textos (quando aplicável)",
    tipo: "ded",
  },
  { key: "escrita_proprio_nome", grupo: "Escrita", label: "Escreve o próprio nome", tipo: "ded" },
  {
    key: "escrita_hipoteses",
    grupo: "Escrita",
    label: "Demonstra avanço nas hipóteses de escrita",
    tipo: "ded",
  },
  {
    key: "escrita_copia",
    grupo: "Escrita",
    label: "Copia palavras e pequenas frases",
    tipo: "ded",
  },
  {
    key: "escrita_espontanea",
    grupo: "Escrita",
    label: "Produz escrita espontânea, mesmo com erros",
    tipo: "ded",
  },
  {
    key: "escrita_coordenacao_motora",
    grupo: "Escrita",
    label: "Demonstra processos na coordenação motora",
    tipo: "ded",
  },
];

export default {
  name: "DesenvolvimentoBibliotecaPage",

  data() {
    return {
      matriculas: [],
      carregandoMatriculas: false,
      matriculaSelecionada: null,
      matricula: null,
      temRegistroSalvo: false,
      salvando: false,
      itens: ITENS,
      grupos: ["Leitura", "Escrita"],
      indicadoresDed: [
        { value: "D", text: "D — Desenvolvido" },
        { value: "ED", text: "ED — Em Desenvolvimento" },
        { value: "ND", text: "ND — Não Desenvolvido" },
      ],
      valores: {},
    };
  },

  computed: {
    algumIndicadorPreenchido() {
      return this.itens.some(
        (item) =>
          this.valores[item.key] &&
          (this.valores[item.key].indicador_s1 || this.valores[item.key].indicador_s2)
      );
    },
    podeLancar() {
      return this.$can(["desenvolvimentoBiblioteca.lancar", "desenvolvimentoBiblioteca.*"]);
    },
    salvarHabilitado() {
      return this.algumIndicadorPreenchido && this.podeLancar;
    },
  },

  mounted() {
    this.valores = this.valoresVazios();
    this.carregarMatriculas();
  },

  methods: {
    valoresVazios() {
      const v = {};
      ITENS.forEach((item) => {
        v[item.key] = { indicador_s1: null, indicador_s2: null };
      });
      return v;
    },

    itemTextAluno(m) {
      return `${m.nome}${m.especial ? " (PcD)" : ""} — ${m.codigo || "sem código"}`;
    },

    itensDoGrupo(grupo) {
      return this.itens.filter((i) => i.grupo === grupo);
    },

    opcoesDoItem() {
      return this.indicadoresDed;
    },

    async carregarMatriculas() {
      this.carregandoMatriculas = true;
      try {
        this.matriculas = await this.$services.desenvolvimentoBibliotecaService.buscarMatriculas(
          null
        );
      } catch (error) {
        this.$handleError(error);
      }
      this.carregandoMatriculas = false;
    },

    async onSelecionarAluno(m) {
      if (!m) {
        this.matricula = null;
        return;
      }
      this.matriculaSelecionada = m;
      await this.carregarRegistros(m.matricula_id);
    },

    async carregarRegistros(matriculaId) {
      this.$loaderService.open("Carregando desenvolvimento...");
      try {
        const payload = await this.$services.desenvolvimentoBibliotecaService.find(matriculaId);
        this.matricula = payload.matricula;
        const valores = this.valoresVazios();
        let temRegistro = false;
        Object.keys(payload.itens || {}).forEach((key) => {
          if (valores[key]) {
            valores[key] = {
              indicador_s1: payload.itens[key].indicador_s1 || null,
              indicador_s2: payload.itens[key].indicador_s2 || null,
            };
            if (valores[key].indicador_s1 || valores[key].indicador_s2) temRegistro = true;
          }
        });
        this.valores = valores;
        this.temRegistroSalvo = temRegistro;
      } catch (error) {
        this.$handleError(error);
      }
      this.$loaderService.close();
    },

    async salvar() {
      if (!this.algumIndicadorPreenchido) {
        this.$toast.error("Preencha ao menos um indicador para salvar.");
        return;
      }
      this.salvando = true;
      try {
        const itens = this.itens.map((item) => ({
          item_key: item.key,
          indicador_s1: this.valores[item.key].indicador_s1,
          indicador_s2: this.valores[item.key].indicador_s2,
        }));
        await this.$services.desenvolvimentoBibliotecaService.salvar({
          matricula_id: this.matriculaSelecionada.matricula_id,
          itens,
        });
        this.temRegistroSalvo = itens.some((i) => i.indicador_s1 || i.indicador_s2);
        this.$toast.success("Desenvolvimento salvo com sucesso");
      } catch (error) {
        this.$handleError(error);
      }
      this.salvando = false;
    },

    async imprimir() {
      this.$loaderService.open("Gerando PDF...");
      try {
        await this.$services.desenvolvimentoBibliotecaService.imprimir(
          this.matriculaSelecionada.matricula_id
        );
      } catch (error) {
        this.$handleError(error);
      }
      this.$loaderService.close();
    },

    async imprimirColetivo() {
      this.$loaderService.open("Gerando PDF coletivo...");
      try {
        await this.$services.desenvolvimentoBibliotecaService.imprimirColetivo();
      } catch (error) {
        this.$handleError(error);
      }
      this.$loaderService.close();
    },
  },
};
</script>

<style scoped>
/* o disabled do Vuetify pinta um cinza translúcido, que sobre o card primary some */
.v-btn.v-btn--disabled.v-btn--has-bg.btn-travado {
  background-color: #9e9e9e !important;
  color: #fafafa !important;
}

.v-btn.v-btn--disabled.v-btn--has-bg.btn-travado .v-icon {
  color: #fafafa !important;
}
</style>
